
import { useMutation } from '@apollo/client';
import { useContext, useState } from 'react';
import { Button, Card, Modal } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga';
import ADD_ORDER_MUTATION from '../../orders/queries/ADD_ORDER_MUTATION';
import Order, { OrderItem } from '../types/Order';
import { AppContext } from '../../store';
import EDIT_ORDER_MUTATION from '../../orders/queries/EDIT_ORDER_MUTATION';
import { GalleryItem } from '../../cms/pages/types/GalleryItem';
import { DUProduct } from '../../cms/products/types/DUProduct';
import AlertDismissible from '../../common/AlertDismissible';
import { EventTypes } from '../../store/actions';
import { numberToCurrency, removeTypename } from '../../utils';


function useURLQuery() {
    return new URLSearchParams(useLocation().search);
}
function orderExists(order: Order | undefined) {
    return order && (order.state === 'new' || order.state === 'submitted');
}
const ProductCard = ({ item }: { item: GalleryItem }) => {
    const query = useURLQuery();
    const navigate = useNavigate();
    const { state, dispatch } = useContext(AppContext);
    const order = state.eventInfo.currentOrder;
    const event = state.eventInfo.currentEvent;
    const stakeholders = event?.stakeholders ? event.stakeholders : [];
    const [zoomShow, setZoomShow] = useState(false);
    const [error, setError] = useState<string>();
    const image = item?.product?.image;
    const url = image?.thumbnail;
    const getOrderInput = () => {
        const eventId = event?.eventId;
        const chapterId = event?.chapterId;
        if (!eventId) return;
        const me = {
            name: state?.accountInfo?.name,
            email: state?.accountInfo?.username,
            constituentId: state?.orgInfo?.constituent_id || '',
            role: 'V',
        }
        const orderInput = orderExists(order) ? { ...order } as Order : {
            description: `Order to support ${eventId} for ${chapterId} from ${me.email}`,
            state: 'new',
            eventId: eventId,
            chapterId: chapterId,
            items: [{ quantity: item.product?.stepQty ? item.product.stepQty : 1, product: item.product as DUProduct }],
            stakeholders: [...stakeholders, me],
        } as Order;
        delete (orderInput as any).updatedAt;
        delete (orderInput as any).createdAt;
        if (order) {
            const existingItem: OrderItem[] = orderInput.items ? orderInput.items.filter(i => i?.product?.code === item?.product?.code) : [];
            if (existingItem.length) {
                const editing = { ...existingItem[0] };
                const product = editing.product;
                const quantity = editing.quantity;
                const step = product.stepQty ? product.stepQty : 1;
                editing.quantity = quantity + step;
                const edited: OrderItem[] = orderInput.items.map((oi: OrderItem) => {
                    const ni = { ...oi };
                    if (ni?.product.code === item?.product?.code) {
                        ni.quantity = quantity + step;
                    }
                    return ni;
                });
                orderInput.items = edited;

            } else {
                const edited = [...orderInput.items, { quantity: item?.product?.stepQty || 1, product: item.product as DUProduct }];
                orderInput.items = edited;
            }
        }
        return removeTypename(orderInput);
    };
    const [addOrder, { loading }] = useMutation(ADD_ORDER_MUTATION, {
        onCompleted: (data: any) => {
            dispatch({
                type: EventTypes.SetOrders,
                payload: { ...state.eventInfo, orders: [...state.eventInfo.orders, data.addOrder,], currentOrder: data.addOrder },
            });
        }
    });
    const [editOrder, { loading: editLoading }] = useMutation(EDIT_ORDER_MUTATION, {
        onCompleted: (data: any) => {
            let changedOrder: Order | null = null;
            const edited = state.eventInfo.orders.map((o: Order) => {
                if (data.editOrder.id === o.id) {
                    changedOrder = { ...data.editOrder };
                    return changedOrder;
                }
                return o;
            });
            dispatch({
                type: EventTypes.SetOrders,
                payload: { ...state.eventInfo, orders: [...edited,], currentOrder: changedOrder },
            });
        }
    });
    const getOrderedQty = () => {
        return order && order.items ? order.items.filter(i => i.product.code === item?.product?.code).map(i => i.quantity).reduce((total, q) => total + q, 0) : 0;
    };
    const handleAddToOrder = async () => {
        ReactGA.event({
            category: "Product",
            action: "add_to_order",
            label: "Add to Order",
            dimension1: item?.product?.code
        });
        if (!orderExists(order)) {
            addOrder({
                variables: {
                    orderData: {
                        ...getOrderInput()
                    },
                },
            });
        } else {
            const maxQty = item?.product?.maxQty;
            const orderedQty = getOrderedQty();
            if (maxQty && orderedQty && orderedQty >= maxQty) {
                setError(`You have ordered the maximum quantity (${orderedQty}) of this product.`);
                return;
            }
            try {
                await editOrder({
                    variables: {
                        orderData: {
                            ...getOrderInput()
                        },
                    },
                });
            } catch (err: any) {
                setError(err.message);
            }
        }
    };

    return <>
        <Card>
            <Card.Img variant="top" style={{ maxWidth: "100%", maxHeight: "300px", width: "unset" }} className="m-auto p-2 cursor-zoom-in" src={url} onClick={() => setZoomShow(true)} />
            <Card.Body>
                {error && <AlertDismissible variant="warning">{error}</AlertDismissible>}
                {item?.product?.code && <Card.Title>{item.product.code} - {item?.product?.name}</Card.Title>}
                {item?.product?.description && <Card.Text dangerouslySetInnerHTML={{ __html: item?.product?.description }}></Card.Text>}
                <Card.Text>Item Cost: <strong>{numberToCurrency(item?.product?.cost || 0)}</strong></Card.Text>
                <Card.Text>
                    {query.get('eventId') && <>
                        <Button variant="outline-secondary" size="sm" onClick={handleAddToOrder} disabled={loading || editLoading}>{loading || editLoading ? 'Saving ...' : 'Add to Order'}</Button>
                        {getOrderedQty() ? <small className="ms-3">{`Quantity: ${getOrderedQty()}`}</small> : null}
                    </>
                    }
                    {!query.get('eventId') && <Button variant="outline-secondary" size="sm" onClick={() => {
                        navigate('/events/');
                    }}>Select Event</Button>}
                </Card.Text>
            </Card.Body>
            {order && getOrderedQty() > 0 && <Card.Footer className="m-0"><Button variant="link" size="sm" className="btn-block" onClick={() => {
                navigate(`/order/${order.id}`);
            }}>Review Order</Button></Card.Footer>}
        </Card>
        <Modal
            dialogClassName="modal-90w"
            show={zoomShow}
            onHide={() => setZoomShow(false)}
            aria-labelledby={`zoom-image-${image?.id}`}
        >
            <Modal.Header closeButton>
                {item.label}
            </Modal.Header>
            <Modal.Body><img src={image?.zoom} className="w-100" alt={item.description} /></Modal.Body>
        </Modal>
    </>
}
export default ProductCard;
